import { TypeManagerDecorator } from "../../main/type.map.service";
import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { CalendarEventDetails, CalendarEventTypeField, CalendarWidget, WidgetType } from "./calendaring.service";
import { EventData, EventService } from "../../services/event.service";
import { CalendaringUtil, ColumnValue, UxConfig } from "./calendaring.util";

@TypeManagerDecorator("s25-ng-calendar-event-details")
@Component({
    selector: "s25-ng-calendar-event-details",
    template: `
        @if (init) {
            <div>
                <div class="evd-header c-margin-bottom--single">
                    <h2 class="ngBold ngInline">
                        {{ eventDetails.reservation?.event?.event_name }}
                    </h2>
                    <span class="ngInline c-margin-left--double">
                        {{ dateStr }}
                    </span>
                </div>
                <div class="kv-grid">
                    @for (item of items; track $index) {
                        <div class="key" [style.color]="uxConfig.detailsLabelColor">
                            {{ item.field.fieldName }}
                        </div>
                        <div class="value">
                            <s25-ng-calendar-column-value
                                [columnValue]="item.columnValue"
                                [reservation]="eventDetails.reservation"
                            ></s25-ng-calendar-column-value>
                        </div>
                    }
                </div>
            </div>
            <div class="evd-footer">
                <button class="btn btn-flat" (click)="closeEventDetails()">
                    <span [style.color]="'#3366cc'">&larr; Back</span>
                </button>
            </div>
        }
    `,
    standalone: false,
    styles: [
        `
            .kv-grid {
                display: grid;
                /* first column hugs the widest key, second takes the rest */
                grid-template-columns: max-content minmax(0, 1fr);
                column-gap: 0.75rem; /* adjust for "natural" breathing room */
                row-gap: 0.5rem;
            }

            .key {
                /* left-align your labels now */
                text-align: left;
                /* drop the right padding—spacing comes from column-gap */
                padding-right: 0;
                font-weight: bold;
            }

            .value {
                text-align: left;
                /* allow the value cell to shrink below its content and wrap */
                min-width: 0;
                white-space: normal;
                overflow-wrap: anywhere;
                /* cap width so large values wrap sooner */
                max-width: 40ch;
            }

            .evd-header {
                padding-bottom: 1.25rem;
                border-bottom: 1px solid #ccc;
            }

            .evd-footer {
                padding-top: 1.25rem;
                margin-top: 1.25rem;
                border-top: 1px solid #ccc;
            }
        `,
    ],
})
export class CalendarEventDetailsComponent implements OnInit {
    @Input() eventDetails: CalendarEventDetails;
    @Output() close = new EventEmitter<void>();

    init = false;
    event: EventData;
    eventDetailsWidget: CalendarWidget;
    uxConfig: UxConfig;
    dateStr: string = "";
    items: {
        field: CalendarEventTypeField;
        columnValue: ColumnValue;
    }[] = [];

    constructor(private cd: ChangeDetectorRef) {}

    closeEventDetails() {
        this.close.emit();
    }

    async ngOnInit() {
        let event: EventData = await EventService.getEventInclude(this.eventDetails.reservation.event.event_id, [
            "attributes",
            "categories",
            "text",
            "customers",
            "reservations",
            "profile",
        ]);
        if (event) {
            let eventType = this.eventDetails.calendar.eventTypes.find(
                (et) => et.eventTypeName === event.event_type_name || et.eventTypeId === event.event_type_id,
            );

            event.custom_attribute = event.custom_attribute || [];

            if (!eventType) {
                // make synthetic event type with all the custom attributes as fields
                eventType = {
                    eventTypeId: -1,
                    eventTypeName: "",
                    eventTypeFields: event.custom_attribute.map((ca) => {
                        return {
                            fieldName: ca.attribute_name,
                            custAtrbId: ca.attribute_id,
                            sortOrder: ca.attribute_sort_order + 99999,
                        };
                    }),
                };
            }

            [
                {
                    fieldName: "Location",
                    systemFieldName: "EventLocation",
                    sortOrder: 0,
                },
                {
                    fieldName: "Event Type",
                    systemFieldName: "EventType",
                    sortOrder: 1,
                },
                {
                    fieldName: "Event Name",
                    systemFieldName: "EventName",
                    sortOrder: 2,
                },
                {
                    fieldName: "Reservation Name",
                    systemFieldName: "ReservationName",
                    sortOrder: 3,
                },
                {
                    fieldName: "Organization",
                    systemFieldName: "EventOrganization",
                    sortOrder: 4,
                },
                {
                    fieldName: "Categories",
                    systemFieldName: "EventCategories",
                    sortOrder: 5,
                },
                {
                    fieldName: "Event Locator",
                    systemFieldName: "EventLocator",
                    sortOrder: 6,
                },
                {
                    fieldName: "Event State",
                    systemFieldName: "EventState",
                    sortOrder: 7,
                },
                {
                    fieldName: "Organizer",
                    systemFieldName: "EventOrganizer",
                    sortOrder: 8,
                },
                {
                    fieldName: "Expected Headcount",
                    systemFieldName: "ExpectedHeadcount",
                    sortOrder: 9,
                },
            ].forEach((field) => {
                if (!eventType.eventTypeFields.find((f) => f.systemFieldName === field.systemFieldName)) {
                    eventType.eventTypeFields.push({
                        fieldName: field.fieldName,
                        systemFieldName: field.systemFieldName,
                        sortOrder: field.sortOrder,
                    });
                }
            });

            // add items for all the event type fields
            this.items = [];
            for (let field of eventType.eventTypeFields) {
                if (field.isHidden) {
                    continue;
                }
                if (field.custAtrbId) {
                    let columnValue = CalendaringUtil.getCustomAttributeValue(
                        this.eventDetails.parentWidget,
                        event.custom_attribute.find((ca) => ca.attribute_id === field.custAtrbId),
                    );
                    if (!columnValue?.value) {
                        continue;
                    }
                    this.items.push({
                        field: field,
                        columnValue: columnValue,
                    });
                } else {
                    let columnValue = CalendaringUtil.getSystemValue(field, this.eventDetails);
                    if (!columnValue?.value) {
                        continue;
                    }
                    this.items.push({
                        field: field,
                        columnValue: columnValue,
                    });
                }
            }
            this.items.sort((a, b) => a.field.sortOrder - b.field.sortOrder);

            this.event = event;
            this.eventDetailsWidget = this.eventDetails.calendar.widgets.find(
                (widget) => widget.widgetType === WidgetType.eventDetailEnhanced,
            );
            if (!this.eventDetailsWidget) {
                this.eventDetailsWidget = this.eventDetails.calendar.widgets.find(
                    (widget) => widget.widgetType === WidgetType.eventDetail,
                );
            }
            if (!this.eventDetailsWidget) {
                this.eventDetailsWidget = this.eventDetails.parentWidget;
            }
            this.uxConfig = CalendaringUtil.resolveUxConfigs(this.eventDetails.calendar, this.eventDetailsWidget);
            this.dateStr = CalendaringUtil.formatDateTimeRangeFully(
                new Date(this.eventDetails.reservation.reservation_start_dt),
                new Date(this.eventDetails.reservation.reservation_end_dt),
                this.eventDetails.calendar,
                this.eventDetailsWidget,
            );
            this.init = true;
            this.cd.detectChanges();
        }
    }
}
